import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AppLayout from '../../components/AppLayout';
import { useAppAuth } from '@/hooks/useAppAuth';
import { exportCareHistoryToCsv } from './utils/exportExcel';

interface CareHistoryItem {
  id: string;
  match_number: string;
  status: string;
  start_date: string;
  end_date: string;
  care_end_date: string;
  location: string;
  hourly_rate: number;
  daily_hours: number;
  total_amount: number;
  completed_at: string;
  patient_name: string;
  patient_phone: string;
}

const FEE_RATE = 0.05;

export default function AppCaregiverHistoryPage() {
  const navigate = useNavigate();
  const auth = useAppAuth(true);
  const [history, setHistory] = useState<CareHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  useEffect(() => {
    if (!auth.userId) return;
    loadHistory();
  }, [auth.userId]);

  async function loadHistory() {
    setLoading(true);
    try {
      const { data: matches } = await supabase
        .from('match_requests')
        .select('*')
        .or(`requester_id.eq.${auth.userId},receiver_id.eq.${auth.userId}`)
        .eq('status', 'completed')
        .order('completed_at', { ascending: false });

      if (!matches || matches.length === 0) {
        setHistory([]);
        setLoading(false);
        return;
      }

      const counterpartIds = matches.map((m) =>
        m.requester_id === auth.userId ? m.receiver_id : m.requester_id,
      );
      const uniqueIds = [...new Set(counterpartIds)];

      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, name, phone')
        .in('id', uniqueIds);
      const profileMap = new Map((profiles || []).map((p) => [p.id, p]));

      const mapped: CareHistoryItem[] = matches.map((m) => {
        const counterpartId = m.requester_id === auth.userId ? m.receiver_id : m.requester_id;
        const cp = profileMap.get(counterpartId);
        return {
          id: m.id,
          match_number: m.match_number || '',
          status: m.status,
          start_date: m.start_date || '',
          end_date: m.end_date || '',
          care_end_date: m.care_end_date || m.completed_at?.split('T')[0] || '',
          location: m.location || '',
          hourly_rate: m.hourly_rate || 0,
          daily_hours: m.daily_hours || 0,
          total_amount: m.total_amount || 0,
          completed_at: m.completed_at || '',
          patient_name: cp?.name || '상대방',
          patient_phone: cp?.phone || '',
        };
      });
      setHistory(mapped);
    } catch (err) {
      console.error('Failed to load care history:', err);
    } finally {
      setLoading(false);
    }
  }

  const availableYears = useMemo(() => {
    const years = new Set<number>();
    history.forEach((h) => {
      const y = new Date(h.completed_at).getFullYear();
      if (!isNaN(y)) years.add(y);
    });
    return [...years].sort((a, b) => b - a);
  }, [history]);

  const filteredHistory = useMemo(() => {
    if (!selectedYear) return history;
    return history.filter((h) => new Date(h.completed_at).getFullYear() === selectedYear);
  }, [history, selectedYear]);

  const yearlyStats = useMemo(() => {
    const totalAmount = filteredHistory.reduce((sum, h) => sum + (h.total_amount || 0), 0);
    const totalFee = Math.round(totalAmount * FEE_RATE);
    const netAmount = totalAmount - totalFee;
    const totalDays = filteredHistory.reduce((sum, h) => {
      if (h.start_date && h.care_end_date) {
        const start = new Date(h.start_date);
        const end = new Date(h.care_end_date);
        const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        return sum + (days > 0 ? days : 0);
      }
      return sum;
    }, 0);
    return { totalAmount, totalFee, netAmount, totalDays, count: filteredHistory.length };
  }, [filteredHistory]);

  if (auth.loading || loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#1976D2] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen px-5 pt-6 pb-24 bg-gray-50">
        {/* 상단 헤더 */}
        <div className="flex items-center gap-3 mb-5">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl bg-white border border-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-50"
          >
            <i className="ri-arrow-left-line text-gray-600" />
          </button>
          <div>
            <h1 className="text-[22px] font-extrabold text-gray-900">간병 이력</h1>
            <p className="text-xs text-gray-400 mt-0.5">완료된 간병 기록을 확인하고 소득 신고에 활용하세요</p>
          </div>
        </div>

        {/* 연도 선택 탭 */}
        {availableYears.length > 0 && (
          <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
            {availableYears.map((year) => (
              <button
                key={year}
                onClick={() => setSelectedYear(year)}
                className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap cursor-pointer transition-all ${
                  selectedYear === year
                    ? 'bg-[#1976D2] text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                {year}년
              </button>
            ))}
          </div>
        )}

        {/* 연도별 요약 통계 카드 */}
        {filteredHistory.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5">
            <h2 className="text-sm font-extrabold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-bar-chart-2-line text-[#1976D2] text-lg" />
              {selectedYear}년 간병 실적 요약
              <button
                onClick={() => exportCareHistoryToCsv(filteredHistory, selectedYear)}
                className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-[#059669] bg-green-50 hover:bg-green-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-excel-2-line text-sm" />
                엑셀 다운로드
              </button>
            </h2>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-[#E3F2FD] rounded-xl p-4">
                <p className="text-[11px] text-[#1976D2] font-bold mb-1">총 간병비</p>
                <p className="text-xl font-extrabold text-[#1976D2]">
                  {(yearlyStats.totalAmount).toLocaleString()}원
                </p>
                <p className="text-[10px] text-[#1976D2]/60 mt-0.5">세전 금액</p>
              </div>
              <div className="bg-[#F3E5F5] rounded-xl p-4">
                <p className="text-[11px] text-[#7B1FA2] font-bold mb-1">중개 수수료</p>
                <p className="text-xl font-extrabold text-[#7B1FA2]">
                  {yearlyStats.totalFee.toLocaleString()}원
                </p>
                <p className="text-[10px] text-[#7B1FA2]/60 mt-0.5">{(FEE_RATE * 100).toFixed(0)}% 공제</p>
              </div>
              <div className="bg-[#E8F5E9] rounded-xl p-4">
                <p className="text-[11px] text-[#2E7D6B] font-bold mb-1">실수령액</p>
                <p className="text-xl font-extrabold text-[#2E7D6B]">
                  {yearlyStats.netAmount.toLocaleString()}원
                </p>
                <p className="text-[10px] text-[#2E7D6B]/60 mt-0.5">세전 기준</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-4">
                <p className="text-[11px] text-amber-700 font-bold mb-1">완료 건수 / 일수</p>
                <p className="text-xl font-extrabold text-amber-700">
                  {yearlyStats.count}건 / {yearlyStats.totalDays}일
                </p>
                <p className="text-[10px] text-amber-700/60 mt-0.5">총 간병일</p>
              </div>
            </div>

            {/* 소득세 안내 */}
            <div className="bg-gray-50 rounded-xl p-3 flex items-start gap-2">
              <i className="ri-information-line text-gray-400 text-sm mt-0.5" />
              <div>
                <p className="text-[11px] text-gray-500 leading-relaxed">
                  위 금액은 세전 기준이며, 실제 소득세 신고 시 필요경비(중개수수료)를 제외한 금액을 기준으로 신고하시면 됩니다.
                  정확한 세금 신고는 세무사와 상담하시길 권장드립니다.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 간병 이력 리스트 */}
        {filteredHistory.length > 0 ? (
          <div>
            <h2 className="text-sm font-extrabold text-gray-900 mb-3">
              완료된 간병 내역 ({filteredHistory.length}건)
            </h2>
            <div className="space-y-3">
              {filteredHistory.map((item) => {
                const fee = Math.round((item.total_amount || 0) * FEE_RATE);
                const net = (item.total_amount || 0) - fee;
                const careDays = (() => {
                  if (item.start_date && item.care_end_date) {
                    const start = new Date(item.start_date);
                    const end = new Date(item.care_end_date);
                    const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                    return days > 0 ? days : 0;
                  }
                  return 0;
                })();

                return (
                  <div
                    key={item.id}
                    className="bg-white rounded-2xl border border-gray-100 p-4"
                  >
                    {/* 상단: 매칭번호 + 완료일 */}
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#E8F5E9] text-[#2E7D6B] font-bold">
                        완료
                      </span>
                      <span className="text-[11px] text-gray-400">
                        매칭번호 {item.match_number}
                      </span>
                    </div>

                    {/* 환자 정보 */}
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-[#E3F2FD] flex items-center justify-center">
                        <i className="ri-user-3-line text-sm text-[#1976D2]" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">{item.patient_name}</p>
                      </div>
                    </div>

                    {/* 간병 정보 그리드 */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="bg-gray-50 rounded-lg p-2.5">
                        <p className="text-[10px] text-gray-400 mb-0.5">간병 기간</p>
                        <p className="text-xs font-bold text-gray-900">
                          {item.start_date} ~ {item.care_end_date}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{careDays}일</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2.5">
                        <p className="text-[10px] text-gray-400 mb-0.5">근무 조건</p>
                        <p className="text-xs font-bold text-gray-900">
                          {item.hourly_rate?.toLocaleString()}원 × {item.daily_hours || '-'}시간
                        </p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{item.location || '위치 미기재'}</p>
                      </div>
                    </div>

                    {/* 금액 정보 */}
                    <div className="border-t border-gray-50 pt-3">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-400">총 간병비</span>
                        <span className="font-bold text-gray-900">{(item.total_amount || 0).toLocaleString()}원</span>
                      </div>
                      <div className="flex items-center justify-between text-xs mt-1">
                        <span className="text-gray-400">중개 수수료 ({(FEE_RATE * 100).toFixed(0)}%)</span>
                        <span className="font-medium text-[#7B1FA2]">-{fee.toLocaleString()}원</span>
                      </div>
                      <div className="flex items-center justify-between text-xs mt-1.5 pt-1.5 border-t border-dashed border-gray-100">
                        <span className="font-bold text-gray-900">실수령액</span>
                        <span className="font-extrabold text-[#2E7D6B]">{net.toLocaleString()}원</span>
                      </div>
                    </div>

                    {/* 완료일 */}
                    <div className="flex items-center gap-1 mt-3 text-[10px] text-gray-400">
                      <i className="ri-check-double-line" />
                      <span>
                        완료일: {new Date(item.completed_at).toLocaleDateString('ko-KR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#E3F2FD] flex items-center justify-center mx-auto mb-4">
              <i className="ri-history-line text-2xl text-[#1976D2]" />
            </div>
            <h3 className="text-base font-extrabold text-gray-900 mb-1">
              {history.length === 0 ? '아직 완료된 간병이 없어요' : `${selectedYear}년에는 완료된 간병이 없어요`}
            </h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              {history.length === 0
                ? '매칭이 성사되고 간병이 완료되면 여기에 기록이 쌓입니다'
                : '다른 연도를 선택해보세요'}
            </p>
            {history.length === 0 && (
              <button
                onClick={() => navigate('/app/caregiver/search')}
                className="mt-4 px-5 py-2.5 rounded-xl bg-[#1976D2] text-white text-sm font-bold cursor-pointer hover:opacity-90"
              >
                환자 찾으러 가기
              </button>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}